import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../../api/adminApi';
import type { UserExperimentView } from '../../../api/userApi';

interface ExperimentsTabProps {
  userId: string;
}

/**
 * Per-user experiment overrides — pin a user to a specific variant, overriding
 * the weight-based bucketing. Mirrors the feature-gate tab but editable.
 * See docs/FeatureDesigns/per-user-experiment-override.md.
 */
export function ExperimentsTab({ userId }: ExperimentsTabProps) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['userExperiments', userId],
    queryFn: () => adminApi.getUserExperiments(userId),
    enabled: !!userId,
  });

  // Local edit state: key → variant ('' = no override).
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!data) return;
    const init: Record<string, string> = {};
    for (const e of data.experiments) if (e.userOverride) init[e.key] = e.userOverride;
    setOverrides(init);
  }, [data]);

  const save = useMutation({
    // Drop empty selections so we only persist real pins.
    mutationFn: () => adminApi.updateUserExperiments(
      userId,
      Object.fromEntries(Object.entries(overrides).filter(([, v]) => v)),
    ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['userExperiments', userId] }),
  });

  if (isLoading) return <div className="loading" style={{ padding: 16 }}>Loading experiments...</div>;

  const experiments: UserExperimentView[] = [...(data?.experiments ?? [])].sort((a, b) => a.key.localeCompare(b.key));
  if (experiments.length === 0) {
    return <p className="no-features-message">No experiments defined. Add them in Settings &rarr; Experiments.</p>;
  }

  const dirty = JSON.stringify(
    Object.fromEntries(Object.entries(overrides).filter(([, v]) => v)),
  ) !== JSON.stringify(
    Object.fromEntries(experiments.filter(e => e.userOverride).map(e => [e.key, e.userOverride])),
  );

  return (
    <div className="features-section">
      <table className="dt dt--compact" style={{ fontSize: 13 }}>
        <thead>
          <tr>
            <th style={{ padding: '6px 10px' }}>Experiment</th>
            <th style={{ padding: '6px 10px' }}>Bucketed</th>
            <th style={{ padding: '6px 10px' }}>Override (pin)</th>
            <th style={{ padding: '6px 10px' }}>Resolved</th>
          </tr>
        </thead>
        <tbody>
          {experiments.map((e) => {
            const value = overrides[e.key] ?? '';
            const resolved = value || e.bucketed || '—';
            return (
              <tr key={e.key}>
                <td style={{ padding: '6px 10px' }}>
                  <strong style={{ fontFamily: 'monospace' }}>{e.key}</strong>
                  {!e.enabled && <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}> (disabled)</span>}
                  {e.description && (
                    <>
                      <br />
                      <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{e.description}</span>
                    </>
                  )}
                </td>
                <td style={{ padding: '6px 10px', color: 'var(--text-tertiary)' }}>{e.bucketed ?? '—'}</td>
                <td style={{ padding: '6px 10px' }}>
                  <select
                    value={value}
                    onChange={(ev) => setOverrides((o) => ({ ...o, [e.key]: ev.target.value }))}
                    disabled={e.variants.length === 0}
                  >
                    <option value="">— none (bucketed) —</option>
                    {e.variants.map((v) => <option key={v} value={v}>{v}</option>)}
                    {/* Preserve a pin to a variant no longer in the definition. */}
                    {value && !e.variants.includes(value) && <option value={value}>{value} (legacy)</option>}
                  </select>
                </td>
                <td style={{ padding: '6px 10px', fontWeight: 600, color: value ? 'var(--color-accent)' : 'var(--text-secondary)' }}>{resolved}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className="btn-primary" onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save overrides'}
        </button>
        {save.isSuccess && !dirty && <span style={{ color: 'var(--color-success, #16a34a)', fontSize: 13 }}>Saved</span>}
        {save.isError && <span style={{ color: 'var(--color-error, #ef4444)', fontSize: 13 }}>{(save.error as Error)?.message || 'Save failed'}</span>}
        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
          A pin forces the variant for this user (even if the experiment is disabled or the variant has weight 0). The user must re-login to pick it up.
        </span>
      </div>
    </div>
  );
}
